import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import {
    addEdge,
    useEdgesState,
    useNodesState,
    type Connection,
    type Edge,
    type NodeTypes,
    Position,
} from "@xyflow/react";
import type {
    EvaluationDomain,
    ResolverConfigDto,
    ResolverInputDto,
    ResolverActionType,
    ResolverAttributeEntry,
    WhenDto,
} from "../../../types/resolver";
import {
    createResolverConfig,
    deleteResolverConfig,
    getResolverConfig,
    setActiveResolverConfig,
    updateResolverConfig,
} from "../../../utils/api";
import {
    createDefaultGraphAction,
    getDefaultActionDetails,
    resolverConfigDtoToGraph,
    resolverInputDtoToDraft,
    toResolverConfigDto,
    type CodeBuilderEntry,
    type KeyValueEntry,
    type ResolverCodeType,
    RESOLVER_CODE_TYPE_OPTIONS,
    type ResolverDraft,
    type ResolverEdgeData,
    type ResolverFlowNode,
    type ResolverGraphNodeData,
    type ResolverInputDraft,
} from "./ResolverBuilder/utils";
import { QuestionSearchAttributeAdder } from "./ResolverBuilder/QuestionSearchAttributeAdder";
import {
    DeleteConfigDialog,
    PasteConfigDialog,
    PasteInputDialog,
} from "./ResolverBuilder/ResolverBuilderDialogs";
import { ResolverConfigToolbar } from "./ResolverBuilder/ResolverConfigToolbar";
import { ResolverFlowCanvas } from "./ResolverBuilder/ResolverFlowCanvas";
import { NodeEdgeDetailsDrawer } from "./ResolverBuilder/NodeEdgeDetailsDrawer";
import { ResolverFlowCard } from "./ResolverBuilder/ResolverFlowCard";
import { ResolverTesterSection } from "./ResolverBuilder/ResolverTesterSection";

const STORAGE_KEY = "qa:resolver-builder:draft";
const DRAFT_DEBOUNCE_MS = 400;

const defaultNodeData: ResolverGraphNodeData = {
    label: "Start Node",
    evaluates: "CODES",
    actions: [createDefaultGraphAction("SET_DERIVED_ATTRIBUTE")],
};

const defaultNodes = (): ResolverFlowNode[] => [
    {
        id: "1",
        position: { x: 140, y: 120 },
        data: defaultNodeData,
        type: "resolverNode",
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
    },
];

type DraftLoadResult = ResolverDraft & { input: ResolverInputDraft; savedAt: number | null };

const readDraft = (): DraftLoadResult => {
    const defaultInput = defaultInputDraft();
    const emptyDraft: DraftLoadResult = {
        nodes: defaultNodes(),
        edges: [] as Array<Edge<ResolverEdgeData>>,
        input: defaultInput,
        savedAt: null,
    };
    let stored: string | null = null;
    if (typeof localStorage !== "undefined") {
        stored = localStorage.getItem(STORAGE_KEY);
    }
    if (!stored && typeof sessionStorage !== "undefined") {
        stored = sessionStorage.getItem(STORAGE_KEY);
    }
    if (!stored) {
        return emptyDraft;
    }
    try {
        const parsed = JSON.parse(stored) as ResolverDraft & { savedAt?: number };
        const savedAt = typeof parsed.savedAt === "number" ? parsed.savedAt : null;
        const normalizedNodes = (parsed?.nodes?.length ? parsed.nodes : defaultNodes()).map((node) => {
            const data = node.data as Partial<ResolverGraphNodeData>;
            const actions =
                Array.isArray(data?.actions) ? data.actions : [createDefaultGraphAction("SET_DERIVED_ATTRIBUTE")];
            return {
                ...node,
                data: {
                    label: data?.label ?? "Node",
                    evaluates: data?.evaluates ?? "CODES",
                    actions,
                },
                targetPosition: Position.Left,
                sourcePosition: Position.Right,
            };
        });
        const rawEdges = (parsed?.edges ?? []) as Array<Edge<ResolverEdgeData>>;
        const normalizedEdges = rawEdges.map((e) => ({
            ...e,
            data: e.data ?? {},
        }));

        const raw = parsed?.input;
        const input: ResolverInputDraft = {
            configName:
                typeof raw?.configName === "string" && raw.configName.trim() !== ""
                    ? raw.configName
                    : defaultInput.configName,
            codes: Array.isArray(raw?.codes) && raw.codes.length > 0 ? raw.codes : defaultInput.codes,
            caseAttributes: Array.isArray(raw?.caseAttributes) ? raw.caseAttributes : defaultInput.caseAttributes,
            caseDerivedAttributes: Array.isArray(raw?.caseDerivedAttributes)
                ? raw.caseDerivedAttributes
                : defaultInput.caseDerivedAttributes,
            workItemDataEntries:
                Array.isArray(raw?.workItemDataEntries) && raw.workItemDataEntries.length > 0
                    ? raw.workItemDataEntries
                    : defaultInput.workItemDataEntries,
        };

        return {
            nodes: normalizedNodes,
            edges: normalizedEdges,
            input,
            savedAt,
        };
    } catch {
        return emptyDraft;
    }
};

const evaluationDomains: EvaluationDomain[] = ["CODES", "CASE", "SPANS", "LOOSE_SPANS"];
const actionTypes: ResolverActionType[] = ["SET_DERIVED_ATTRIBUTE", "SET_ANSWER", "ADD_CODE", "THROW_ERROR", "MARK_CODE_AS_IDEAL"];
const codeTypeOptions: ResolverCodeType[] = RESOLVER_CODE_TYPE_OPTIONS;
/** Expected work item field names (matches WorkItemDto / resolver workItemData). */
const WORK_ITEM_FIELD_NAMES = [
    "patientClass",
    "visitReason",
    "serviceLine",
    "healthSystemTenantId",
    "healthSystemCode",
    "insurance",
    "caseCategory",
    "examDescription",
    "patientName",
    "patientGender",
    "hospitalAccountRecord",
    "facility",
    "referringProviderName",
    "performingProviderName",
    "billingProviderName",
    "caseId",
    "caseJsonId",
    "tenantUid",
    "processInstanceGuid",
    "caseRunUid",
    "ruleId",
    "ruleName",
    "ruleCollectionId",
    "matchedRuleCriteria",
    "matchedRuleValue",
    "importId",
] as const;

const defaultWorkItemTemplate: Record<string, string> = {
    patientClass: "INPATIENT",
    visitReason: "SCREENING",
    serviceLine: "QA",
};

const defaultInputDraft = (): ResolverInputDraft => ({
    configName: "Resolver Builder Draft",
    codes: [
        {
            id: "code-1",
            codeType: "DIAGNOSIS",
            code: "C34.90",
            original: true,
            units: "",
            attributes: [],
            derivedAttributes: [],
        },
    ],
    caseAttributes: [],
    caseDerivedAttributes: [],
    workItemDataEntries: Object.entries(defaultWorkItemTemplate).map(([key, value], index) => ({
        id: `workitem-${index + 1}`,
        key,
        value,
    })),
});

const makeEntry = (): KeyValueEntry => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    key: "",
    value: "",
});
const parseLiteral = (value: string): string | number | boolean => {
    const trimmed = value.trim();
    if (trimmed.toLowerCase() === "true") {
        return true;
    }
    if (trimmed.toLowerCase() === "false") {
        return false;
    }
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric) && trimmed.length > 0) {
        return numeric;
    }
    return trimmed;
};

export interface ResolverBuilderPanelProps {
    /** When provided, list can filter by rubric version and "Set as active" is shown. */
    rubricVersionId?: number;
}

const ResolverBuilderPanel: React.FC<ResolverBuilderPanelProps> = ({ rubricVersionId }) => {
    const initialDraft = useMemo(() => readDraft(), []);
    const [lastLocalSaveAt, setLastLocalSaveAt] = useState<number | null>(initialDraft.savedAt);
    const persistDraftTimerRef = useRef<number | undefined>(undefined);
    const [configName, setConfigName] = useState(initialDraft.input.configName);
    const [configDescription, setConfigDescription] = useState("");
    const [nodes, setNodes] = useNodesState<ResolverFlowNode>(initialDraft.nodes);
    const [edges, setEdges] = useEdgesState(initialDraft.edges);
    const [codes, setCodes] = useState<CodeBuilderEntry[]>(initialDraft.input.codes);
    const [caseAttributes, setCaseAttributes] = useState<ResolverAttributeEntry[]>(initialDraft.input.caseAttributes);
    const [caseDerivedAttributes, setCaseDerivedAttributes] = useState<KeyValueEntry[]>(
        initialDraft.input.caseDerivedAttributes
    );
    const [workItemDataEntries, setWorkItemDataEntries] = useState<KeyValueEntry[]>(
        initialDraft.input.workItemDataEntries
    );
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
    const [pasteConfigDialogOpen, setPasteConfigDialogOpen] = useState(false);
    const [pasteInputDialogOpen, setPasteInputDialogOpen] = useState(false);
    const [copySnackbarMessage, setCopySnackbarMessage] = useState<string | null>(null);

    // Config load, save
    const [loadedConfigId, setLoadedConfigId] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [setActiveError, setSetActiveError] = useState<string | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const selectedNode = useMemo(() => nodes.find((node) => node.id === selectedNodeId), [nodes, selectedNodeId]);
    const selectedEdge = useMemo(
        () => (edges as Array<Edge<ResolverEdgeData>>).find((e) => e.id === selectedEdgeId),
        [edges, selectedEdgeId]
    );

    const declaredDerivedAttributeNames = useMemo(() => {
        const names = new Set<string>();
        nodes.forEach((node) => {
            node.data?.actions?.forEach((action) => {
                if (action.action === "SET_DERIVED_ATTRIBUTE") {
                    const name = action.actionDetails?.derivedAttributeName;
                    if (name != null && String(name).trim() !== "") {
                        names.add(String(name));
                    }
                }
            });
        });
        return Array.from(names);
    }, [nodes]);

    const inputBuilderDerivedAttributeNames = useMemo(
        () => [...declaredDerivedAttributeNames],
        [declaredDerivedAttributeNames]
    );

    const existingValuesByDerivedAttributeName = useMemo(() => {
        const byName: Record<string, unknown[]> = {};
        const add = (name: string, val: unknown) => {
            if (!name.trim()) return;
            const key = name.trim();
            if (!byName[key]) byName[key] = [];
            byName[key].push(val);
        };
        nodes.forEach((node) => {
            node.data?.actions?.forEach((action) => {
                if (action.action === "SET_DERIVED_ATTRIBUTE") {
                    const name = action.actionDetails?.derivedAttributeName;
                    const val = action.actionDetails?.derivedAttributeValue;
                    if (name != null && String(name).trim()) add(String(name), val);
                }
                if (action.action === "ADD_CODE") {
                    const derived = action.actionDetails?.derivedAttributes as Record<string, unknown> | undefined;
                    if (derived && typeof derived === "object")
                        Object.entries(derived).forEach(([k, v]) => add(k, v));
                }
            });
        });
        return byName;
    }, [nodes]);

    const edgeDrawerEvaluationDomain = useMemo(() => {
        if (!selectedEdge) return undefined;
        const sourceNode = nodes.find((n) => n.id === selectedEdge.source);
        return sourceNode?.data?.evaluates;
    }, [nodes, selectedEdge]);
    const generatedInput = useMemo(() => {
        const workItemData = workItemDataEntries.reduce<Record<string, unknown>>((acc, entry) => {
            if (!entry.key.trim()) {
                return acc;
            }
            acc[entry.key.trim()] = parseLiteral(entry.value);
            return acc;
        }, {});

        return {
            codes: codes
                .filter((entry) => entry.code.trim().length > 0)
                .map((entry, index) => ({
                    codeType: entry.codeType,
                    id: index + 1,
                    code: entry.code.trim(),
                    original: entry.original,
                    sequenceNumber: index + 1,
                    units:
                        entry.codeType === "PROCEDURE" && entry.units.trim().length > 0
                            ? Number(entry.units)
                            : undefined,
                    attributes: entry.attributes.map(({ id: _id, answerOptions: _o, ...dto }) => dto),
                })),
            caseAttributes: caseAttributes.map(({ id: _id, answerOptions: _o, ...dto }) => dto),
            workItemData,
            looseSpans: [],
        };
    }, [codes, caseAttributes, workItemDataEntries]);
    const generatedConfig = useMemo(() => toResolverConfigDto(nodes, edges, configName), [nodes, edges, configName]);
    const nodeTypes = useMemo(() => ({ resolverNode: ResolverFlowCard } as NodeTypes), []);

    useEffect(() => {
        if (persistDraftTimerRef.current !== undefined) {
            window.clearTimeout(persistDraftTimerRef.current);
        }
        persistDraftTimerRef.current = window.setTimeout(() => {
            const savedAt = Date.now();
            const payload = {
                nodes,
                edges,
                input: {
                    configName,
                    codes,
                    caseAttributes,
                    caseDerivedAttributes,
                    workItemDataEntries,
                },
                savedAt,
            };
            try {
                if (typeof localStorage !== "undefined") {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
                }
                setLastLocalSaveAt(savedAt);
            } catch {
                /* quota or private mode */
            }
        }, DRAFT_DEBOUNCE_MS);
        return () => {
            if (persistDraftTimerRef.current !== undefined) {
                window.clearTimeout(persistDraftTimerRef.current);
            }
        };
    }, [nodes, edges, configName, codes, caseAttributes, caseDerivedAttributes, workItemDataEntries]);

    const handleLoadConfig = useCallback(async (selectedConfig: ResolverConfigDto) => {
        const id = selectedConfig?.id;
        if (id == null) return;
        setLoadError(null);
        try {
            const config = await getResolverConfig(id);
            const { nodes: nextNodes, edges: nextEdges } = resolverConfigDtoToGraph(config);
            setConfigName(typeof config.name === "string" ? config.name : "Resolver Builder Draft");
            setConfigDescription(config.description ?? "");
            setNodes(nextNodes);
            setEdges(nextEdges);
            setLoadedConfigId(config.id ?? id);
            const defaultInput = defaultInputDraft();
            setCodes(defaultInput.codes);
            setCaseAttributes(defaultInput.caseAttributes);
            setCaseDerivedAttributes(defaultInput.caseDerivedAttributes);
            setWorkItemDataEntries(defaultInput.workItemDataEntries);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : "Failed to load config.");
        }
    }, [setNodes, setEdges]);

    const handleNew = useCallback(() => {
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.removeItem(STORAGE_KEY);
            }
            if (typeof sessionStorage !== "undefined") {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            /* ignore */
        }
        setLastLocalSaveAt(null);
        const defaultInput = defaultInputDraft();
        setConfigName(defaultInput.configName);
        setConfigDescription("");
        setNodes(defaultNodes());
        setEdges([]);
        setLoadedConfigId(null);
        setCodes(defaultInput.codes);
        setCaseAttributes(defaultInput.caseAttributes);
        setCaseDerivedAttributes(defaultInput.caseDerivedAttributes);
        setWorkItemDataEntries(defaultInput.workItemDataEntries);
        setLoadError(null);
        setSaveError(null);
        setSaveSuccess(false);
    }, [setNodes, setEdges]);

    const handleSave = useCallback(async () => {
        const config = toResolverConfigDto(nodes, edges, configName);
        if (!configName.trim()) {
            setSaveError("Name is required.");
            return;
        }
        setSaveError(null);
        setSaveSuccess(false);
        setIsSaving(true);
        try {
            const payload = {
                name: configName.trim(),
                description: configDescription.trim() || undefined,
                config: { ...config, id: undefined },
            };
            if (loadedConfigId != null) {
                const updated = await updateResolverConfig(loadedConfigId, {
                    name: payload.name,
                    description: payload.description,
                    config: payload.config,
                });
                setLoadedConfigId(updated.id ?? loadedConfigId);
            } else {
                const created = await createResolverConfig(payload);
                setLoadedConfigId(created.id ?? null);
            }
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save.");
        } finally {
            setIsSaving(false);
        }
    }, [nodes, edges, configName, configDescription, loadedConfigId]);

    const handleSetActive = useCallback(async () => {
        if (rubricVersionId == null || loadedConfigId == null) return;
        setSetActiveError(null);
        try {
            await setActiveResolverConfig({ rubricVersionId, resolverConfigId: loadedConfigId });
        } catch (err) {
            setSetActiveError(err instanceof Error ? err.message : "Failed to set active.");
        }
    }, [rubricVersionId, loadedConfigId]);

    const handleDelete = useCallback(async () => {
        if (loadedConfigId == null) return;
        setSaveError(null);
        try {
            await deleteResolverConfig(loadedConfigId);
            setDeleteConfirmOpen(false);
            handleNew();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to delete.");
        }
    }, [loadedConfigId, handleNew]);

    const onConnect = (connection: Connection) => {
        setEdges((existingEdges) => {
            const next = addEdge(connection, existingEdges ?? []);
            const list = Array.isArray(next) ? next : (existingEdges ?? []);
            return list.map((e: Edge<ResolverEdgeData>) => ({
                ...e,
                data: e.data ?? {},
            }));
        });
    };

    const updateSelectedEdgeData = (patch: Partial<ResolverEdgeData>) => {
        if (!selectedEdgeId) return;
        setEdges((prev) =>
            (prev as Array<Edge<ResolverEdgeData>>).map((e) =>
                e.id === selectedEdgeId ? { ...e, data: { ...e.data, ...patch } } : e
            )
        );
    };

    const addNode = () => {
        const nextNumericId = nodes.reduce((max, node) => {
            const parsed = Number(node.id);
            return Number.isFinite(parsed) ? Math.max(max, parsed) : max;
        }, 0) + 1;
        const nextId = String(nextNumericId);
        const newNode: ResolverFlowNode = {
            id: nextId,
            position: { x: 140 + nodes.length * 48, y: 120 + nodes.length * 36 },
            data: {
                label: `Node ${nextId}`,
                evaluates: "CODES",
                actions: [createDefaultGraphAction("SET_DERIVED_ATTRIBUTE")],
            },
            type: "resolverNode",
            targetPosition: Position.Left,
            sourcePosition: Position.Right,
        };
        const updatedNodes = [...nodes, newNode];
        setNodes(updatedNodes);
        setSelectedNodeId(newNode.id);
    };

    const updateSelectedNodeData = (patch: Partial<ResolverGraphNodeData>) => {
        if (!selectedNode) {
            return;
        }
        const updatedNodes = nodes.map((node) =>
            node.id === selectedNode.id
                ? {
                    ...node,
                    data: {
                        ...node.data,
                        ...patch,
                    },
                }
                : node
        );
        setNodes(updatedNodes);
    };

    const actionsList = selectedNode?.data?.actions ?? [];

    const updateSelectedNodeAction = (
        actionId: string,
        patch: {
            action?: ResolverActionType;
            actionDetails?: Record<string, unknown>;
            rules?: { when?: WhenDto } | null;
        }
    ) => {
        if (!selectedNode) return;
        const nextActions = actionsList.map((a) =>
            a.id === actionId
                ? {
                    ...a,
                    ...(patch.action !== undefined && { action: patch.action }),
                    ...(patch.actionDetails !== undefined && { actionDetails: patch.actionDetails }),
                    ...(patch.rules !== undefined && { rules: patch.rules ?? undefined }),
                  }
                : a
        );
        updateSelectedNodeData({ actions: nextActions });
    };

    const addActionToSelectedNode = () => {
        if (!selectedNode) return;
        const nextActions = [...actionsList, createDefaultGraphAction("SET_DERIVED_ATTRIBUTE")];
        updateSelectedNodeData({ actions: nextActions });
    };

    const removeActionFromSelectedNode = (actionId: string) => {
        if (!selectedNode) return;
        const nextActions = actionsList.filter((a) => a.id !== actionId);
        updateSelectedNodeData({ actions: nextActions });
    };

    const moveActionInSelectedNode = (actionId: string, direction: "up" | "down") => {
        if (!selectedNode) return;
        const idx = actionsList.findIndex((a) => a.id === actionId);
        if (idx < 0) return;
        const j = direction === "up" ? idx - 1 : idx + 1;
        if (j < 0 || j >= actionsList.length) return;
        const next = [...actionsList];
        [next[idx], next[j]] = [next[j], next[idx]];
        updateSelectedNodeData({ actions: next });
    };

    const deleteSelectedNode = () => {
        if (!selectedNodeId || nodes.length <= 1) return;
        setNodes((prev) => prev.filter((n) => n.id !== selectedNodeId));
        setEdges((prev) => prev.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
    };

    const deleteSelectedEdge = () => {
        if (!selectedEdgeId) return;
        setEdges((prev) => prev.filter((e) => e.id !== selectedEdgeId));
        setSelectedEdgeId(null);
    };

    const addCode = () => {
        const nextId = `code-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setCodes((existing) => [
            ...existing,
            { id: nextId, codeType: "DIAGNOSIS", code: "", original: true, units: "", attributes: [], derivedAttributes: [] },
        ]);
    };
    const removeCode = (id: string) => {
        setCodes((existing) => existing.filter((code) => code.id !== id));
    };
    const moveCode = (id: string, direction: "up" | "down") => {
        setCodes((existing) => {
            const idx = existing.findIndex((code) => code.id === id);
            if (idx < 0) return existing;
            const target = direction === "up" ? idx - 1 : idx + 1;
            if (target < 0 || target >= existing.length) return existing;
            const next = [...existing];
            [next[idx], next[target]] = [next[target], next[idx]];
            return next;
        });
    };
    const updateCode = (id: string, patch: Partial<CodeBuilderEntry>) => {
        setCodes((existing) => existing.map((code) => (code.id === id ? { ...code, ...patch } : code)));
    };
    const addCodeAttributeEntry = (codeId: string, entry: ResolverAttributeEntry) => {
        setCodes((existing) =>
            existing.map((code) =>
                code.id === codeId ? { ...code, attributes: [...code.attributes, entry] } : code
            )
        );
    };
    const updateCodeAttributeEntry = (
        codeId: string,
        attributeId: string,
        patch: Partial<Pick<ResolverAttributeEntry, "answerValueId" | "answerDisplayText">>
    ) => {
        setCodes((existing) =>
            existing.map((code) =>
                code.id === codeId
                    ? {
                          ...code,
                          attributes: code.attributes.map((a) =>
                              a.id === attributeId ? { ...a, ...patch } : a
                          ),
                      }
                    : code
            )
        );
    };
    const removeCodeAttributeEntry = (codeId: string, attributeId: string) => {
        setCodes((existing) =>
            existing.map((code) =>
                code.id === codeId
                    ? { ...code, attributes: code.attributes.filter((a) => a.id !== attributeId) }
                    : code
            )
        );
    };

    const addCodeActionAttributes = (actionId: string, entry: ResolverAttributeEntry) => {
        const action = selectedNode?.data?.actions?.find((a) => a.id === actionId);
        if (!action) return;
        const attrs = (action.actionDetails?.attributes as ResolverAttributeEntry[]) ?? [];
        updateSelectedNodeAction(actionId, {
            actionDetails: { ...action.actionDetails, attributes: [...attrs, entry] },
        });
    };
    const updateCodeActionAttributeEntry = (
        actionId: string,
        attributeId: string,
        patch: Partial<Pick<ResolverAttributeEntry, "answerValueId" | "answerDisplayText">>
    ) => {
        const action = selectedNode?.data?.actions?.find((a) => a.id === actionId);
        if (!action) return;
        const attrs = ((action.actionDetails?.attributes as ResolverAttributeEntry[]) ?? []).map((a) =>
            a.id === attributeId ? { ...a, ...patch } : a
        );
        updateSelectedNodeAction(actionId, {
            actionDetails: { ...action.actionDetails, attributes: attrs },
        });
    };
    const removeCodeActionAttributeEntry = (actionId: string, attributeId: string) => {
        const action = selectedNode?.data?.actions?.find((a) => a.id === actionId);
        if (!action) return;
        const attrs = ((action.actionDetails?.attributes as ResolverAttributeEntry[]) ?? []).filter(
            (a) => a.id !== attributeId
        );
        updateSelectedNodeAction(actionId, {
            actionDetails: { ...action.actionDetails, attributes: attrs },
        });
    };
    const updateCodeActionDerivedAttributes = (actionId: string, derivedAttributes: Record<string, unknown>) => {
        const action = selectedNode?.data?.actions?.find((a) => a.id === actionId);
        if (!action) return;
        updateSelectedNodeAction(actionId, {
            actionDetails: { ...action.actionDetails, derivedAttributes },
        });
    };

    const addCaseAttributeEntry = (entry: ResolverAttributeEntry) => {
        setCaseAttributes((existing) => [...existing, entry]);
    };
    const updateCaseAttributeEntry = (
        attributeId: string,
        patch: Partial<Pick<ResolverAttributeEntry, "answerValueId" | "answerDisplayText">>
    ) => {
        setCaseAttributes((existing) =>
            existing.map((a) => (a.id === attributeId ? { ...a, ...patch } : a))
        );
    };
    const removeCaseAttributeEntry = (attributeId: string) => {
        setCaseAttributes((existing) => existing.filter((a) => a.id !== attributeId));
    };
  
    const usedWorkItemFields = useMemo(
        () => new Set(workItemDataEntries.map((e) => e.key).filter(Boolean)),
        [workItemDataEntries]
    );
    const availableWorkItemFields = useMemo(
        () => WORK_ITEM_FIELD_NAMES.filter((name) => !usedWorkItemFields.has(name)),
        [usedWorkItemFields]
    );
    const addWorkItemField = () => {
        const nextKey = availableWorkItemFields[0] ?? "";
        setWorkItemDataEntries((existing) => [...existing, { ...makeEntry(), key: nextKey }]);
    };
    const updateWorkItemField = (id: string, patch: Partial<KeyValueEntry>) => {
        setWorkItemDataEntries((existing) =>
            existing.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry))
        );
    };
    const removeWorkItemField = (id: string) => {
        setWorkItemDataEntries((existing) => existing.filter((entry) => entry.id !== id));
    };

    const handlePasteConfigConfirm = useCallback(
        (config: ResolverConfigDto) => {
            const { nodes: nextNodes, edges: nextEdges } = resolverConfigDtoToGraph(config);
            setConfigName(typeof config.name === "string" ? config.name : "Resolver Builder Draft");
            setNodes(nextNodes);
            setEdges(nextEdges);
            setPasteConfigDialogOpen(false);
        },
        []
    );

    const handlePasteInputConfirm = useCallback((input: ResolverInputDto) => {
        const draft = resolverInputDtoToDraft(input);
        setCodes(draft.codes as CodeBuilderEntry[]);
        setCaseAttributes(draft.caseAttributes);
        setCaseDerivedAttributes(draft.caseDerivedAttributes as KeyValueEntry[]);
        setWorkItemDataEntries(draft.workItemDataEntries as KeyValueEntry[]);
        setPasteInputDialogOpen(false);
    }, []);

    const handlePasteConfigDialogOpen = () => setPasteConfigDialogOpen(true);
    const handlePasteInputDialogOpen = () => setPasteInputDialogOpen(true);

    const handleCopyConfig = async () => {
        try {
            const json = JSON.stringify(generatedConfig, null, 2);
            await navigator.clipboard.writeText(json);
            setCopySnackbarMessage("Config copied to clipboard");
        } catch {
            setCopySnackbarMessage("Failed to copy config");
        }
    };

    const handleCopyInput = async () => {
        try {
            const json = JSON.stringify(generatedInput, null, 2);
            await navigator.clipboard.writeText(json);
            setCopySnackbarMessage("Input copied to clipboard");
        } catch {
            setCopySnackbarMessage("Failed to copy input");
        }
    };

    return (
        <Stack spacing={2}>
            <ResolverConfigToolbar
                rubricVersionId={rubricVersionId}
                loadedConfigId={loadedConfigId}
                configName={configName}
                isSaving={isSaving}
                loadError={loadError}
                saveError={saveError}
                saveSuccess={saveSuccess}
                setActiveError={setActiveError}
                onLoad={handleLoadConfig}
                onNew={handleNew}
                onSave={handleSave}
                onSetActive={handleSetActive}
                onDeleteClick={() => setDeleteConfirmOpen(true)}
                onDismissLoadError={() => setLoadError(null)}
                onDismissSaveError={() => setSaveError(null)}
                onDismissSaveSuccess={() => setSaveSuccess(false)}
                onDismissSetActiveError={() => setSetActiveError(null)}
            />
            {lastLocalSaveAt != null && (
                <Typography variant="caption" color="text.secondary" data-testid="resolver-draft-autosave-caption">
                    Draft autosaved in this browser · {new Date(lastLocalSaveAt).toLocaleString()}
                </Typography>
            )}

            <ResolverFlowCanvas
                nodes={nodes}
                edges={edges as Array<Edge<ResolverEdgeData>>}
                setNodes={setNodes}
                setEdges={setEdges as React.Dispatch<React.SetStateAction<Edge<ResolverEdgeData>[]>>}
                configName={configName}
                configDescription={configDescription}
                onConfigNameChange={setConfigName}
                onConfigDescriptionChange={setConfigDescription}
                selectedNodeId={selectedNodeId}
                selectedEdgeId={selectedEdgeId}
                onSelectedNodeIdChange={setSelectedNodeId}
                onSelectedEdgeIdChange={setSelectedEdgeId}
                onAddNode={addNode}
                onPasteConfig={handlePasteConfigDialogOpen}
                onCopyConfig={handleCopyConfig}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
            />

            <ResolverTesterSection
                codes={codes}
                addCode={addCode}
                removeCode={removeCode}
                moveCode={moveCode}
                updateCode={updateCode}
                addCodeAttributeEntry={addCodeAttributeEntry}
                removeCodeAttributeEntry={removeCodeAttributeEntry}
                updateCodeAttributeEntry={updateCodeAttributeEntry}
                caseAttributes={caseAttributes}
                addCaseAttributeEntry={addCaseAttributeEntry}
                removeCaseAttributeEntry={removeCaseAttributeEntry}
                updateCaseAttributeEntry={updateCaseAttributeEntry}
                workItemDataEntries={workItemDataEntries}
                addWorkItemField={addWorkItemField}
                removeWorkItemField={removeWorkItemField}
                updateWorkItemField={updateWorkItemField}
                availableWorkItemFields={availableWorkItemFields}
                generatedInput={generatedInput}
                generatedConfig={generatedConfig}
                onPasteInput={handlePasteInputDialogOpen}
                onCopyInput={handleCopyInput}
            />

            <PasteConfigDialog
                open={pasteConfigDialogOpen}
                onClose={() => setPasteConfigDialogOpen(false)}
                onConfirm={handlePasteConfigConfirm}
            />
            <PasteInputDialog
                open={pasteInputDialogOpen}
                onClose={() => setPasteInputDialogOpen(false)}
                onConfirm={handlePasteInputConfirm}
            />

            <Snackbar
                open={copySnackbarMessage != null}
                autoHideDuration={3000}
                onClose={() => setCopySnackbarMessage(null)}
                message={copySnackbarMessage ?? undefined}
                data-testid="copy-snackbar"
            />

            <DeleteConfigDialog
                open={deleteConfirmOpen}
                configName={configName}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
            />

            <NodeEdgeDetailsDrawer
                open={Boolean(selectedNode) || Boolean(selectedEdgeId)}
                selectedNode={selectedNode ?? null}
                selectedEdge={selectedEdge ?? null}
                nodesCount={nodes.length}
                declaredDerivedAttributeNames={declaredDerivedAttributeNames}
                edgeDrawerEvaluationDomain={edgeDrawerEvaluationDomain}
                existingValuesByDerivedAttributeName={existingValuesByDerivedAttributeName}
                evaluationDomains={evaluationDomains}
                actionTypes={actionTypes}
                codeTypeOptions={codeTypeOptions}
                onClose={() => {
                    setSelectedNodeId(null);
                    setSelectedEdgeId(null);
                }}
                onUpdateNodeData={updateSelectedNodeData}
                onUpdateNodeAction={updateSelectedNodeAction}
                onAddAction={addActionToSelectedNode}
                onRemoveAction={removeActionFromSelectedNode}
                onMoveAction={moveActionInSelectedNode}
                onUpdateEdgeData={updateSelectedEdgeData}
                onDeleteNode={deleteSelectedNode}
                onDeleteEdge={deleteSelectedEdge}
                onUpdateCodeActionAttributeEntry={updateCodeActionAttributeEntry}
                onRemoveCodeActionAttributeEntry={removeCodeActionAttributeEntry}
                onAddCodeActionAttributes={addCodeActionAttributes}
                onUpdateCodeActionDerivedAttributes={updateCodeActionDerivedAttributes}
            />
        </Stack>
    );
};

export default ResolverBuilderPanel;
